import { useState } from 'react'
import {
  UserCheck,
  UserPlus,
  Clock,
  AlertCircle,
  BarChart3,
  MapPin,
  Calendar,
  Shield,
  Search,
  Download,
  CheckCircle,
  LogOut,
  Eye,
  Settings,
  Bell,
  Phone,
  Mail,
  Smartphone,
  Camera,
  Zap,
  TrendingUp,
} from 'lucide-react'

export default function VisitorManagementPage() {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLocation, setSelectedLocation] = useState('all')
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedVisitor, setSelectedVisitor] = useState(null)
  const [visitors, setVisitors] = useState([
    {
      id: 1,
      name: 'Sarah Johnson',
      company: 'TechCorp Solutions',
      purpose: 'Business Meeting',
      checkIn: '09:15 AM',
      checkOut: 'Active',
      location: 'Main Building',
      badge: 'V-2024-001',
      photo: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=0284c7&color=fff',
      host: 'John Smith',
    },
    {
      id: 2,
      name: 'Michael Chen',
      company: 'Digital Innovations Inc',
      purpose: 'Partnership Discussion',
      checkIn: '08:45 AM',
      checkOut: '11:30 AM',
      location: 'Conference Room A',
      badge: 'V-2024-002',
      photo: 'https://ui-avatars.com/api/?name=Michael+Chen&background=0284c7&color=fff',
      host: 'Emily Davis',
    },
    {
      id: 3,
      name: 'Jessica Martinez',
      company: 'Marketing Pro',
      purpose: 'Contract Signing',
      checkIn: '10:00 AM',
      checkOut: 'Active',
      location: 'Executive Suite',
      badge: 'V-2024-003',
      photo: 'https://ui-avatars.com/api/?name=Jessica+Martinez&background=0284c7&color=fff',
      host: 'Robert Wilson',
    },
    {
      id: 4,
      name: 'David Kumar',
      company: 'Cloud Systems Ltd',
      purpose: 'Technical Training',
      checkIn: '07:30 AM',
      checkOut: '12:00 PM',
      location: 'Training Center',
      badge: 'V-2024-004',
      photo: 'https://ui-avatars.com/api/?name=David+Kumar&background=0284c7&color=fff',
      host: 'Lisa Anderson',
    },
  ])
  const [expectedVisitors, setExpectedVisitors] = useState([
    { id: 1, name: 'Amanda Peterson', company: 'Global Tech', time: '02:00 PM', host: 'Mike Johnson' },
    { id: 2, name: 'Carlos Rodriguez', company: 'Finance Plus', time: '02:30 PM', host: 'Sarah Lee' },
    { id: 3, name: 'Emma Thompson', company: 'Design House', time: '03:00 PM', host: 'Tom Brown' },
    { id: 4, name: 'James Wilson', company: 'Consulting Plus', time: '03:30 PM', host: 'Jennifer White' },
  ])
  const [editFormData, setEditFormData] = useState({})

  // Mock stats data
  const stats = {
    totalVisitors: 1247,
    visitorsToday: 34,
    checkedIn: visitors.filter(v => v.checkOut === 'Active').length,
    expectedArrivals: expectedVisitors.length,
    avgDuration: '2h 34m',
    securityAlerts: 3,
  }

  // Handler functions
  const handleViewClick = (visitor) => {
    setSelectedVisitor(visitor)
    setShowViewModal(true)
  }

  const handleEditClick = (visitor) => {
    setSelectedVisitor(visitor)
    setEditFormData({ ...visitor })
    setShowEditModal(true)
  }

  const handleCheckOut = (visitorId) => {
    const currentTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    setVisitors(visitors.map(v =>
      v.id === visitorId ? { ...v, checkOut: currentTime } : v
    ))
  }

  const handleCheckIn = (visitorId) => {
    const currentTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    const expectedVisitor = expectedVisitors.find(v => v.id === visitorId)
    if (expectedVisitor) {
      const newVisitor = {
        id: Math.max(...visitors.map(v => v.id)) + 1,
        name: expectedVisitor.name,
        company: expectedVisitor.company,
        purpose: `Meeting with ${expectedVisitor.host}`,
        checkIn: currentTime,
        checkOut: 'Active',
        location: 'Main Building',
        badge: `V-2024-${Math.random().toString().slice(2, 6)}`,
        photo: `https://ui-avatars.com/api/?name=${expectedVisitor.name}&background=0284c7&color=fff`,
        host: expectedVisitor.host,
      }
      setVisitors([...visitors, newVisitor])
      setExpectedVisitors(expectedVisitors.filter(v => v.id !== visitorId))
    }
  }

  const handleSaveEdit = () => {
    setVisitors(visitors.map(v =>
      v.id === editFormData.id ? editFormData : v
    ))
    setShowEditModal(false)
    setEditFormData({})
  }

  const filteredVisitors = visitors.filter(v =>
    v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.company.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const securityAlerts = [
    { id: 1, type: 'Badge Mismatch', visitor: 'Unknown Person', time: '11:45 AM', severity: 'High' },
    { id: 2, type: 'Extended Overstay', visitor: 'John Doe', time: '11:30 AM', severity: 'Medium' },
    { id: 3, type: 'Restricted Area Access', visitor: 'Jane Smith', time: '11:15 AM', severity: 'Critical' },
  ]

  const locations = [
    { name: 'Main Building', visitors: 12, capacity: 200 },
    { name: 'Executive Suite', visitors: 3, capacity: 50 },
    { name: 'Conference Room A', visitors: 2, capacity: 30 },
    { name: 'Training Center', visitors: 5, capacity: 100 },
    { name: 'Lobby', visitors: 8, capacity: 100 },
  ]

  const features = [
    {
      icon: UserCheck,
      title: 'AI-Powered Check-In',
      description: 'Automated facial recognition and biometric verification for seamless visitor entry',
    },
    {
      icon: Shield,
      title: 'Badge Generation',
      description: 'Instant digital and physical badge creation with secure QR codes and RFID integration',
    },
    {
      icon: AlertCircle,
      title: 'Real-Time Alerts',
      description: 'Immediate security notifications for unauthorized access or policy violations',
    },
    {
      icon: MapPin,
      title: 'Location Tracking',
      description: 'Real-time GPS and indoor positioning system for visitor location monitoring',
    },
    {
      icon: Calendar,
      title: 'Appointment Management',
      description: 'Pre-registration and automated visitor confirmations with calendar sync',
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reporting',
      description: 'Comprehensive visitor analytics, trends, and compliance reporting dashboards',
    },
    {
      icon: Phone,
      title: 'Notification System',
      description: 'SMS/Email alerts to hosts about visitor arrivals and check-outs in real-time',
    },
    {
      icon: Camera,
      title: 'Surveillance Integration',
      description: 'Link to security camera feeds and incident documentation for complete audit trails',
    },
  ]

  const capabilities = [
    { name: 'Facial Recognition', status: 'Active', accuracy: '99.2%' },
    { name: 'Biometric Verification', status: 'Active', accuracy: '98.8%' },
    { name: 'Badge Scanning', status: 'Active', success: '99.9%' },
    { name: 'Access Control', status: 'Active', denials: '0' },
    { name: 'Real-time Tracking', status: 'Active', accuracy: '99.5%' },
    { name: 'Threat Detection', status: 'Active', detection: '98.1%' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-black text-white mb-2">Visitor Management System</h1>
            <p className="text-slate-400">AI-Driven Professional Access & Check-In Platform</p>
          </div>
          <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all">
            <UserPlus size={20} className="inline mr-2" />
            New Check-In
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Visitors (30d)</p>
                <p className="text-2xl font-black text-white">{stats.totalVisitors}</p>
              </div>
              <UserCheck size={32} className="text-blue-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Today's Visitors</p>
                <p className="text-2xl font-black text-white">{stats.visitorsToday}</p>
              </div>
              <Calendar size={32} className="text-cyan-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Currently Checked In</p>
                <p className="text-2xl font-black text-white">{stats.checkedIn}</p>
              </div>
              <CheckCircle size={32} className="text-green-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Expected Arrivals</p>
                <p className="text-2xl font-black text-white">{stats.expectedArrivals}</p>
              </div>
              <TrendingUp size={32} className="text-purple-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg Duration</p>
                <p className="text-2xl font-black text-white">{stats.avgDuration}</p>
              </div>
              <Clock size={32} className="text-orange-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-red-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Security Alerts</p>
                <p className="text-2xl font-black text-red-400">{stats.securityAlerts}</p>
              </div>
              <AlertCircle size={32} className="text-red-400 opacity-50" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-blue-500/20 pb-4">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { id: 'visitors', label: 'Active Visitors', icon: UserCheck },
          { id: 'expected', label: 'Expected Arrivals', icon: Calendar },
          { id: 'alerts', label: 'Security Alerts', icon: AlertCircle },
          { id: 'locations', label: 'Locations', icon: MapPin },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-600/40 to-cyan-600/40 text-blue-200 border border-blue-500/50'
                : 'text-slate-400 hover:text-blue-300'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Features Grid */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">Core Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-6 rounded-lg border border-blue-500/20 hover:border-blue-500/40 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-600/20 rounded-lg">
                      <feature.icon size={24} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">{feature.title}</h3>
                      <p className="text-sm text-slate-400">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Capabilities */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">AI Capabilities Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {capabilities.map((cap, idx) => (
                <div key={idx} className="bg-slate-800/50 p-4 rounded-lg border border-green-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-white">{cap.name}</h3>
                    <span className="text-xs px-2 py-1 bg-green-600/20 text-green-300 rounded-full">
                      {cap.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">
                      {cap.accuracy || cap.success || cap.denials}
                    </span>
                    <CheckCircle size={16} className="text-green-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Active Visitors Tab */}
      {activeTab === 'visitors' && (
        <div className="space-y-6">
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search visitors..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-2 bg-slate-800/50 border border-blue-500/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500/50"
              />
            </div>
            <button className="px-4 py-2 bg-blue-600/20 text-blue-300 rounded-lg hover:bg-blue-600/30 transition-all">
              <Download size={20} />
            </button>
          </div>

          <div className="space-y-4">
            {filteredVisitors.map(visitor => (
              <div key={visitor.id} className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20 hover:border-blue-500/40 transition-all">
                <div className="flex items-start justify-between">
                  <div className="flex gap-4 flex-1">
                    <img
                      src={visitor.photo}
                      alt={visitor.name}
                      className="w-16 h-16 rounded-lg border border-blue-500/30"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-white">{visitor.name}</h3>
                        <span className="text-xs px-2 py-1 bg-blue-600/20 text-blue-300 rounded">{visitor.badge}</span>
                      </div>
                      <p className="text-sm text-slate-400">{visitor.company}</p>
                      <div className="flex gap-4 mt-3 text-sm">
                        <span className="text-slate-300">
                          <span className="text-slate-400">Purpose:</span> {visitor.purpose}
                        </span>
                        <span className="text-slate-300">
                          <span className="text-slate-400">Location:</span> {visitor.location}
                        </span>
                        <span className="text-slate-300">
                          <span className="text-slate-400">Host:</span> {visitor.host}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400 mb-2">Check-In: {visitor.checkIn}</p>
                    {visitor.checkOut === 'Active' ? (
                      <span className="inline-block text-xs px-3 py-1 bg-green-600/20 text-green-300 rounded-full">Active</span>
                    ) : (
                      <span className="inline-block text-xs px-3 py-1 bg-slate-600/20 text-slate-300 rounded-full">
                        Left: {visitor.checkOut}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleViewClick(visitor)}
                    className="px-3 py-1 text-xs bg-blue-600/20 text-blue-300 rounded hover:bg-blue-600/30 transition-all"
                  >
                    <Eye size={14} className="inline mr-1" />
                    View
                  </button>
                  <button
                    onClick={() => handleCheckOut(visitor.id)}
                    disabled={visitor.checkOut !== 'Active'}
                    className={`px-3 py-1 text-xs rounded transition-all ${
                      visitor.checkOut === 'Active'
                        ? 'bg-orange-600/20 text-orange-300 hover:bg-orange-600/30'
                        : 'bg-slate-600/20 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    <LogOut size={14} className="inline mr-1" />
                    Check-Out
                  </button>
                  <button
                    onClick={() => handleEditClick(visitor)}
                    className="px-3 py-1 text-xs bg-slate-700/20 text-slate-300 rounded hover:bg-slate-700/30 transition-all"
                  >
                    <Settings size={14} className="inline mr-1" />
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expected Arrivals Tab */}
      {activeTab === 'expected' && (
        <div className="space-y-4">
          {expectedVisitors.map(visitor => (
            <div key={visitor.id} className="bg-slate-800/50 p-4 rounded-lg border border-blue-500/20 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">{visitor.name}</h3>
                <p className="text-sm text-slate-400">{visitor.company}</p>
                <p className="text-xs text-slate-500 mt-1">Host: {visitor.host}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-blue-300">{visitor.time}</p>
                <button
                  onClick={() => handleCheckIn(visitor.id)}
                  className="mt-2 px-4 py-2 bg-green-600/20 text-green-300 text-sm rounded hover:bg-green-600/30 transition-all"
                >
                  Check-In Ready
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Security Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {securityAlerts.map(alert => (
            <div
              key={alert.id}
              className={`bg-slate-800/50 p-4 rounded-lg border-l-4 flex items-center justify-between ${
                alert.severity === 'Critical'
                  ? 'border-l-red-500 border border-red-500/20'
                  : alert.severity === 'High'
                  ? 'border-l-orange-500 border border-orange-500/20'
                  : 'border-l-yellow-500 border border-yellow-500/20'
              }`}
            >
              <div>
                <h3 className="font-bold text-white">{alert.type}</h3>
                <p className="text-sm text-slate-400">{alert.visitor}</p>
              </div>
              <div className="text-right">
                <span
                  className={`text-xs px-3 py-1 rounded-full font-semibold ${
                    alert.severity === 'Critical'
                      ? 'bg-red-600/20 text-red-300'
                      : alert.severity === 'High'
                      ? 'bg-orange-600/20 text-orange-300'
                      : 'bg-yellow-600/20 text-yellow-300'
                  }`}
                >
                  {alert.severity}
                </span>
                <p className="text-xs text-slate-400 mt-1">{alert.time}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Locations Tab */}
      {activeTab === 'locations' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((location, idx) => (
            <div key={idx} className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-white text-lg">{location.name}</h3>
                <MapPin size={24} className="text-blue-400" />
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-400">Occupancy</span>
                    <span className="text-sm font-semibold text-white">
                      {location.visitors}/{location.capacity}
                    </span>
                  </div>
                  <div className="w-full bg-slate-700/50 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-blue-600 to-cyan-500 h-2 rounded-full"
                      style={{ width: `${(location.visitors / location.capacity) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <p className="text-xs text-slate-400">
                  {Math.round((location.visitors / location.capacity) * 100)}% capacity
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Visitor Modal */}
      {showViewModal && selectedVisitor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-2xl w-full mx-4 p-8">
            <div className="flex items-center gap-6 mb-6">
              <img
                src={selectedVisitor.photo}
                alt={selectedVisitor.name}
                className="w-24 h-24 rounded-lg border border-blue-500/30"
              />
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{selectedVisitor.name}</h2>
                <p className="text-slate-400">{selectedVisitor.company}</p>
                <p className="text-sm text-slate-500 mt-1">Badge: {selectedVisitor.badge}</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Purpose</p>
                  <p className="text-white">{selectedVisitor.purpose}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Location</p>
                  <p className="text-white">{selectedVisitor.location}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Check-In Time</p>
                  <p className="text-white">{selectedVisitor.checkIn}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  <p className={selectedVisitor.checkOut === 'Active' ? 'text-green-300' : 'text-slate-400'}>
                    {selectedVisitor.checkOut === 'Active' ? 'Active' : `Left: ${selectedVisitor.checkOut}`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Host</p>
                  <p className="text-white">{selectedVisitor.host}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-slate-700/20 text-slate-300 rounded hover:bg-slate-700/30 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Visitor Modal */}
      {showEditModal && selectedVisitor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-2xl w-full mx-4 p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Edit Visitor Details</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Name</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={e => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-500/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Company</label>
                  <input
                    type="text"
                    value={editFormData.company}
                    onChange={e => setEditFormData({ ...editFormData, company: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Host</label>
                  <input
                    type="text"
                    value={editFormData.host}
                    onChange={e => setEditFormData({ ...editFormData, host: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Purpose</label>
                <input
                  type="text"
                  value={editFormData.purpose}
                  onChange={e => setEditFormData({ ...editFormData, purpose: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-500/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Location</label>
                  <input
                    type="text"
                    value={editFormData.location}
                    onChange={e => setEditFormData({ ...editFormData, location: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Check-In Time</label>
                  <input
                    type="text"
                    value={editFormData.checkIn}
                    onChange={e => setEditFormData({ ...editFormData, checkIn: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditFormData({})
                }}
                className="px-4 py-2 bg-slate-700/20 text-slate-300 rounded hover:bg-slate-700/30 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded hover:shadow-lg hover:shadow-blue-500/50 transition-all"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
